import React, { useState, useRef, useEffect } from 'react';
import { GithubIcon, DollarSign, Users, Star, GitFork, Box, Share2, Camera, Trophy } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client - replace with your credentials
const supabase = createClient(
  'https://zdbeanvrlfqafdaljcic.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InpkYmVhbnZybGZxYWZkYWxqY2ljIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzAwOTY2NTUsImV4cCI6MjA0NTY3MjY1NX0.zBdzNi9PIKYYi_v4qmxn_YKSkwRjzz2wdSGZHF_L6EM'
);

const App = () => {
  const [username, setUsername] = useState('');
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [topUsers, setTopUsers] = useState([]);
  const resultCardRef = useRef(null);

  // Fetch top users on component mount
  useEffect(() => {
    fetchTopUsers();
  }, []);

  const calculateWorth = (data) => {
    const {
      followers,
      public_repos,
      public_gists,
      created_at,
      starred_repositories
    } = data;

    const accountAge = (new Date() - new Date(created_at)) / (1000 * 60 * 60 * 24 * 365);
    
    let worth = 0;
    worth += followers * 5;
    worth += public_repos * 10;
    worth += public_gists * 3;
    worth += accountAge * 100;
    worth += starred_repositories * 2;

    return Math.round(worth);
  };

  const fetchTopUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('github_users')
        .select('*')
        .order('worth', { ascending: false })
        .limit(5);

      if (error) throw error;
      setTopUsers(data);
    } catch (err) {
      console.error('Error fetching top users:', err);
    }
  };

  const saveUserToSupabase = async (userData, worth) => {
    try {
      const userRecord = {
        username: userData.login,
        name: userData.name,
        avatar_url: userData.avatar_url,
        worth: worth,
        followers: userData.followers,
        public_repos: userData.public_repos,
        starred_repositories: userData.starred_repositories,
        last_calculated: new Date().toISOString()
      };

      const { error } = await supabase
        .from('github_users')
        .upsert(userRecord, { onConflict: 'username' });

      if (error) throw error;
      
      // Refresh the leaderboard
      fetchTopUsers();
    } catch (err) {
      console.error('Error saving user data:', err);
    }
  };

  const fetchGithubData = async () => {
    if (!username) return;
    
    setLoading(true);
    setError('');
    try {
      const [userResponse, reposResponse] = await Promise.all([
        fetch(`https://api.github.com/users/${username}`),
        fetch(`https://api.github.com/users/${username}/repos`)
      ]);

      if (!userResponse.ok) {
        throw new Error('User not found');
      }

      const userData = await userResponse.json();
      const reposData = await reposResponse.json();
      const totalStars = reposData.reduce((acc, repo) => acc + repo.stargazers_count, 0);
      
      const enrichedUserData = {
        ...userData,
        starred_repositories: totalStars
      };

      setUserData(enrichedUserData);
      
      // Calculate worth and save to Supabase
      const worth = calculateWorth(enrichedUserData);
      await saveUserToSupabase(enrichedUserData, worth);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const shareOnTwitter = () => {
    const worth = calculateWorth(userData);
    const tweetText = encodeURIComponent(
      `🚀 My GitHub worth is $${worth.toLocaleString()}! Calculate yours at GitHub Worth Calculator! #GitHub #Developer`
    );
    window.open(`https://twitter.com/intent/tweet?text=${tweetText}`, '_blank');
  };

  const captureScreenshot = async () => {
    setIsCapturing(true);
    if (resultCardRef.current) {
      resultCardRef.current.classList.add('capturing');
    }
    await new Promise(resolve => setTimeout(resolve, 500));
    if (resultCardRef.current) {
      resultCardRef.current.classList.remove('capturing');
    }
    setIsCapturing(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 flex items-center justify-center gap-3">
            <GithubIcon className="w-10 h-10" />
            GitHub Worth Calculator
          </h1>
          <p className="text-gray-400">Discover your GitHub profile's worth based on your contributions and impact</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Left Column - Leaderboard */}
          <div className="bg-gray-800 rounded-xl p-6 shadow-xl">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Trophy className="text-yellow-400" />
              Top Developers
            </h2>
            <div className="space-y-4">
              {topUsers.map((user, index) => (
                <div key={user.username} className="flex items-center gap-3 p-2 bg-gray-900 rounded-lg">
                  <div className="text-lg font-bold text-yellow-400 w-6">#{index + 1}</div>
                  <img src={user.avatar_url} alt={user.username} className="w-8 h-8 rounded-full" />
                  <div className="flex-1">
                    <div className="font-medium">{user.name || user.username}</div>
                    <div className="text-sm text-gray-400">${user.worth.toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column - Calculator */}
          <div className="md:col-span-2">
            {/* Search Input */}
            <div className="flex gap-4 mb-8">
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter GitHub username"
                className="flex-1 px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:outline-none focus:border-blue-500 transition-colors"
              />
              <button
                onClick={fetchGithubData}
                disabled={loading || !username}
                className="px-6 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Calculating...' : 'Calculate'}
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="text-red-400 text-center mb-8">
                {error}
              </div>
            )}

            {/* Results Card */}
            {userData && (
              <div 
                ref={resultCardRef}
                className={`bg-gray-800 rounded-xl p-6 shadow-xl animate-fade-in relative ${
                  isCapturing ? 'scale-95 opacity-80 transition-all duration-200' : ''
                }`}
              >
                <div className="flex items-center gap-6 mb-6">
                  <img
                    src={userData.avatar_url}
                    alt={username}
                    className="w-24 h-24 rounded-full border-4 border-blue-500"
                  />
                  <div>
                    <h2 className="text-2xl font-bold">{userData.name || username}</h2>
                    <p className="text-gray-400">{userData.bio || 'No bio available'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <Users className="text-blue-400" />
                    <span>{userData.followers} followers</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Box className="text-blue-400" />
                    <span>{userData.public_repos} repositories</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Star className="text-blue-400" />
                    <span>{userData.starred_repositories} stars received</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <GitFork className="text-blue-400" />
                    <span>{userData.public_gists} gists</span>
                  </div>
                </div>

                <div className="text-center p-6 bg-gray-900 rounded-lg mb-6">
                  <div className="text-gray-400 mb-2">Estimated Worth</div>
                  <div className="text-4xl font-bold text-green-400 flex items-center justify-center gap-2">
                    <DollarSign className="w-8 h-8" />
                    {calculateWorth(userData).toLocaleString()}
                  </div>
                </div>

                {/* Share Buttons */}
                <div className="flex justify-center gap-4">
                  <button
                    onClick={shareOnTwitter}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-400 hover:bg-blue-500 rounded-lg transition-colors"
                  >
                    <Share2 className="w-5 h-5" />
                    Share on Twitter
                  </button>
                  <button
                    onClick={captureScreenshot}
                    disabled={isCapturing}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Camera className="w-5 h-5" />
                    {isCapturing ? 'Capturing...' : 'Take Screenshot'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default App;
